import { supabase } from "@/lib/supabase/browser-client"
import { Tables } from "@/supabase/types"
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js"
import { Dispatch, SetStateAction } from "react"
import { printObject } from '@/lib/utils'
import { HandlerConfig } from "../base-subscription-manager"
import { getFileById, getFileWorkspacesByFileId } from "@/db/files"
import { v4 as uuidv4 } from "uuid"
import { createClientLogger } from "@/utils/logger/clientLogger"

const logger = createClientLogger('realtime.files', supabase)

type Row = Tables<'files'>
type WsRow = Tables<'file_workspaces'>

const appliesToWorkspace = async (workspaceId:string | null, id: string): Promise<boolean> => {
	if (!workspaceId) return false
	const fileWorkspaces = await getFileWorkspacesByFileId(supabase, id)
	return fileWorkspaces.workspaces.some(ws => ws.id === workspaceId)
}

/**
 * This method doesn't handle insert since at that time the workspaces are not known.
 * It also doesn't handle delete since workspace relationship join table is better suited since it covers all cases
 * @param workspaceId
 * @param setFiles
 * @returns
 */
export function makeFileChangeHandler(
	setFiles: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<Row> {
	const uuid = uuidv4()
	return {
		key: `file-changes-${uuid}`,
		channelName: `file-changes-${uuid}`,
		events: ['UPDATE'],
		schema: 'public',
		table: 'files',
		handler: async (payload: RealtimePostgresChangesPayload<Row>) => {
			if (payload.eventType === 'UPDATE') {
				const file = payload.new as Row
				//here not checking for workspace since will update only if found and it avoid async db call
				setFiles(prevFiles => {
					const exists = prevFiles.some(f => f.id === file.id)
					if (!exists) return prevFiles // No change, no re-render
					logger.info(`Handling file update:${printObject(file)}`)
					return prevFiles.map(prevItem => prevItem.id === file.id ? file : prevItem)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}

/**
 * This method doesn't handle update since the relationship join table is not designed to mutate in that way
 * @param workspaceId
 * @param setFiles
 * @returns
 */
export function makeFileWorkspaceHandler(
	workspaceIdRef: React.RefObject<string | null>,
	setFiles: Dispatch<SetStateAction<Row[]>>
): HandlerConfig<WsRow> {
	const uuid = uuidv4()
	return {
		key: `file-ws-changes-${uuid}`,
		channelName: `file-ws-changes-${uuid}`,
		events: ['INSERT', 'DELETE'],
		schema: 'public',
		table: 'file_workspaces',
		handler: async (payload: RealtimePostgresChangesPayload<WsRow>) => {
			const workspaceId = workspaceIdRef.current
			if (payload.eventType == "INSERT") {
				const fileWs = payload.new as WsRow
				const id = fileWs.file_id
				if (await appliesToWorkspace(workspaceId, id)) {
					const file = await getFileById(supabase, id)
					if (file) {
						setFiles(prev => {
 							if (prev.some(p => p.id === file.id)) return prev
							logger.info(`Handling file Insert:${printObject(file)}`)
 							return [...prev, file]
						})
					}
				}
			}
			else if (payload.eventType == "DELETE") {
				const fileWs = payload.old as WsRow
				const id = fileWs.file_id
				setFiles(prev => {
					const exists = prev.some(p => p.id === id)
					if (!exists) return prev // No change, no re-render
					logger.info(`Handling file Delete:${id}`)
					return prev.filter(p => p.id !== id)
				})
			}
		},
		inactivityThreshold: 1_800_000
	}
}